import React, { useState } from 'react';
import CountryPicker from './CountryPicker';
import {
  parsePhoneInput,
  looksLikeInternationalInput,
  countries,
} from '../utils/phoneCanonical';
import { upsertPhoneNumberStub, claimNumber } from '../services/firestoreService';

const defaultCountry = countries.find((c) => c.iso2 === 'AU') || countries[0];

/**
 * Claim flow: parse number → ensure Firestore stub → call claimNumber API.
 */
function ClaimPhoneForm({ onClaimed, presetCanonicalId, compact = false }) {
  const [phoneInput, setPhoneInput] = useState(
    presetCanonicalId ? `+${presetCanonicalId}` : ''
  );
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      let parsed;
      if (presetCanonicalId && !phoneInput.trim()) {
        parsed = { canonicalId: presetCanonicalId, displayE164: `+${presetCanonicalId}` };
      } else {
        parsed = parsePhoneInput(phoneInput, selectedCountry);
        if (parsed.error) {
          setError(parsed.error);
          return;
        }
        if (
          looksLikeInternationalInput(phoneInput.trim(), phoneInput.replace(/\D/g, '')) &&
          parsed.country &&
          parsed.country.iso2 !== selectedCountry.iso2
        ) {
          setSelectedCountry(parsed.country);
        }
      }

      await upsertPhoneNumberStub(parsed);
      const result = await claimNumber(parsed.canonicalId, {
        countryCallingCode: parsed.countryCallingCode || parsed.country?.callingCode,
        nationalNumber: parsed.nationalNumber,
        displayE164: parsed.displayE164,
        countryName: parsed.countryName || parsed.country?.name,
        iso2: parsed.iso2 || parsed.country?.iso2,
      });

      onClaimed?.(result);
    } catch (err) {
      setError(err.message || 'Could not claim this number');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={compact ? 'space-y-3' : 'space-y-4'}>
      {!presetCanonicalId && (
        <div className="flex flex-col sm:flex-row gap-2">
          <CountryPicker
            value={selectedCountry.iso2}
            onChange={setSelectedCountry}
            disabled={submitting}
          />
          <input
            type="tel"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            placeholder="Phone number"
            className="flex-1 border rounded px-3 py-2"
            disabled={submitting}
          />
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
      >
        {submitting ? 'Claiming…' : presetCanonicalId ? 'Claim this number' : 'Claim number'}
      </button>

      {!compact && (
        <p className="text-xs text-gray-500">
          Use your full international number (e.g. +61 412 345 678). We will create the listing if
          it does not exist yet, then ask you to verify ownership.
        </p>
      )}
    </form>
  );
}

export default ClaimPhoneForm;
